import { useMemo } from 'react';
import { X, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type {
  UploadProgressEvent,
  UploadProgressPhase,
} from '@/services/data-sources/upload-sources';

export type UploadProgressMap = Map<string, UploadProgressEvent>;

interface UploadProgressListProps {
  progressMap: UploadProgressMap;
  onCancel?: () => void;
  canCancel?: boolean;
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const phaseLabel = (phase: UploadProgressPhase): string => {
  switch (phase) {
    case 'queued':
      return 'Queued';
    case 'uploading':
      return 'Uploading';
    case 'completing':
      return 'Processing';
    case 'done':
      return 'Done';
    case 'failed':
      return 'Failed';
    case 'canceled':
      return 'Canceled';
  }
};

const phaseColor = (phase: UploadProgressPhase): string => {
  switch (phase) {
    case 'done':
      return 'bg-green-500';
    case 'failed':
      return 'bg-red-500';
    case 'canceled':
      return 'bg-gray-400';
    case 'completing':
      return 'bg-blue-500';
    case 'uploading':
      return 'bg-primary';
    default:
      return 'bg-gray-300';
  }
};

const PhaseIcon = ({ phase }: { phase: UploadProgressPhase }) => {
  if (phase === 'done')
    return <CheckCircle2 className='h-4 w-4 text-green-600' />;
  if (phase === 'failed')
    return <AlertCircle className='h-4 w-4 text-red-600' />;
  if (phase === 'canceled') return <X className='h-4 w-4 text-gray-500' />;
  if (phase === 'uploading' || phase === 'completing')
    return <Loader2 className='h-4 w-4 animate-spin text-blue-600' />;
  return <Loader2 className='h-4 w-4 text-gray-400' />;
};

export const UploadProgressList = ({
  progressMap,
  onCancel,
  canCancel = false,
}: UploadProgressListProps) => {
  const items = useMemo(() => Array.from(progressMap.values()), [progressMap]);

  if (!items.length) return null;

  const totalBytes = items.reduce((sum, i) => sum + i.size, 0);
  const weightedPercent =
    totalBytes === 0
      ? 0
      : Math.round(
          (items.reduce((sum, i) => sum + (i.percent / 100) * i.size, 0) /
            totalBytes) *
            100,
        );
  const doneCount = items.filter((i) => i.phase === 'done').length;

  return (
    <div className='flex flex-col gap-3 border rounded-md p-3 bg-muted/30'>
      <div className='flex items-center justify-between gap-2'>
        <div className='flex flex-col'>
          <span className='text-sm font-medium'>
            Uploading {doneCount}/{items.length} file
            {items.length === 1 ? '' : 's'}
          </span>
          <span className='text-xs text-muted-foreground'>
            {weightedPercent}% · {formatBytes(totalBytes)} total
          </span>
        </div>
        {canCancel && onCancel && (
          <Button
            id='data-sources-upload-cancel'
            variant='outline'
            size='sm'
            onClick={onCancel}
          >
            Cancel upload
          </Button>
        )}
      </div>

      <div className='flex flex-col gap-2 max-h-60 overflow-y-auto'>
        {items.map((item) => (
          <div key={item.fileId} className='flex flex-col gap-1'>
            <div className='flex items-center gap-2 text-sm min-w-0'>
              <PhaseIcon phase={item.phase} />
              <span className='truncate flex-1' title={item.filename}>
                {item.filename}
              </span>
              <span className='text-xs text-muted-foreground whitespace-nowrap'>
                {formatBytes(item.size)}
              </span>
              <span className='text-xs font-medium whitespace-nowrap w-32 text-right tabular-nums'>
                {phaseLabel(item.phase)}
                {item.phase === 'uploading' || item.phase === 'completing'
                  ? ` ${item.percent}%`
                  : ''}
              </span>
            </div>
            <div className='h-1.5 bg-gray-200 rounded-full overflow-hidden'>
              <div
                className={cn(
                  'h-full transition-all duration-200',
                  phaseColor(item.phase),
                )}
                style={{
                  width: `${
                    item.phase === 'done'
                      ? 100
                      : item.phase === 'failed' || item.phase === 'canceled'
                        ? 0
                        : item.percent
                  }%`,
                }}
              />
            </div>
            {item.error && (
              <span
                className='text-xs text-red-600 truncate'
                title={item.error}
              >
                {item.error}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
